import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { fetchInterplayShell, useInterplayShellStore } from '@/interplay/shellStore';
import { interplayPeopleApi, isNotFound, type Ownership } from '@/api/endpoints/interplayPeople';
import { hostBrands, interplayPeopleEnabled, peopleKeys, type HostBrand } from './model';

const FIVE_MIN = 5 * 60 * 1000;

/**
 * Product code → Console brand (name, logo, monogram) for the "Synced from <host>" words. The ET
 * bar's shell is reused when it is loaded; otherwise the same read (`/interplay/shell`) is made
 * once per company. 404/503 → no brands (the words say "a connected product").
 */
export function useHostBrands(): Record<string, HostBrand> {
  const enabled = interplayPeopleEnabled();
  const companyId = useAuthStore((s) => s.activeCompanyId);
  const loaded = useInterplayShellStore((s) => s.shell);
  const { data } = useQuery({
    queryKey: peopleKeys.hosts(companyId),
    queryFn: async () => {
      const r = await fetchInterplayShell();
      return r.kind === 'shell' ? hostBrands(r.shell) : {};
    },
    enabled: enabled && !loaded && !!companyId,
    staleTime: FIVE_MIN,
  });
  if (!enabled) return {};
  return loaded ? hostBrands(loaded) : data ?? {};
}

/** A 404 (server flag off) or any failure reads as "not referenced": nothing locks or renders. */
async function safeOwnership(get: () => Promise<{ data: Ownership }>): Promise<Ownership | null> {
  try {
    const r = await get();
    return r.data && typeof r.data === 'object' ? r.data : null;
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
}

/** Ownership of one employee (HR). Null while off, not referenced, or the server flag is off. */
export function useOwnership(employeeId: string | null | undefined, opts: { enabled?: boolean } = {}) {
  const enabled = interplayPeopleEnabled() && !!employeeId && (opts.enabled ?? true);
  return useQuery({
    queryKey: peopleKeys.ownership(employeeId ?? ''),
    queryFn: () => safeOwnership(() => interplayPeopleApi.ownership(employeeId!)),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/** The signed-in employee's own ownership (ESS). */
export function useMyOwnership(opts: { enabled?: boolean } = {}) {
  const enabled = interplayPeopleEnabled() && (opts.enabled ?? true);
  return useQuery({
    queryKey: peopleKeys.myOwnership,
    queryFn: () => safeOwnership(() => interplayPeopleApi.myOwnership()),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/** Referenced = the host owns some fields (only then does anything new render). */
export function referenced(o: Ownership | null | undefined): o is Ownership {
  return !!o && o.referenced === true && Array.isArray(o.owners) && o.owners.length > 0;
}
